import { h, Fragment } from "preact";
import { useEffect, useRef, useState, useCallback } from "preact/hooks";
import useProductGroup from "../helpers/useProductGroup/useProductGroup";
import ProductCardSwatch from "./product-card-swatch";
import { caretNext, caretPrevious } from "../helpers/icons";

const spaceBetween = () =>
  window.matchMedia("(min-width: 1024px)").matches ? 24 : 15;

export default function ProductCardSwatches({
  groupName,
  familyIds,
  familyData,
  productId,
  cardEl,
}) {
  const [productGroup] = useProductGroup(groupName, familyIds, productId, familyData);
  const [activeProductId, _setActiveProductId] = useState(productId);
  const swiperRef = useRef(null);
  const swatchesRef = useRef(null);
  const prevEl = useRef(null);
  const nextEl = useRef(null);
  const showArrows = productGroup.length > 3;

  const setActiveProductId = useCallback(
    (newId) => {
      _setActiveProductId(newId);

      swatchesRef.current?.dispatchEvent(
        new CustomEvent("product-card:update", {
          bubbles: true,
          detail: {
            product: productGroup.find((product) => product.id === newId),
          },
        })
      );
    },
    [_setActiveProductId, swatchesRef, productGroup, productId]
  );

  // Set initial product ID when data is loaded
  useEffect(() => {
    setActiveProductId(productId);
  }, [productGroup]);

  // Reset to original product on mouseleave
  useEffect(() => {
    const onMouseLeave = (e) => {
      if (e instanceof CustomEvent) return;
      setActiveProductId(productId);
    };
    cardEl.addEventListener("mouseleave", onMouseLeave);

    return () => cardEl.removeEventListener("mouseleave", onMouseLeave);
  }, [setActiveProductId]);

  // Init swiper
  useEffect(() => {
    if (!(swiperRef.current && prevEl.current && nextEl.current)) return;

    const swiperParams = {
      spaceBetween: spaceBetween(),
      // This isn't working with Swiper Element for some reason...
      // breakpoints: {
      //   1024: {
      //     spaceBetween: 24,
      //   },
      // },
      navigation: {
        prevEl: prevEl.current,
        nextEl: nextEl.current,
      },
    };

    Object.assign(swiperRef.current, swiperParams);

    swiperRef.current.initialize();
  }, [swiperRef.current, prevEl.current, nextEl.current, productGroup]);

  useEffect(() => {
    const handleResize = () => {
      if (!swiperRef.current) return;

      swiperRef.current.spaceBetween = window.matchMedia("(min-width: 1024px)")
        .matches
        ? 24
        : 15;
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [swiperRef.current]);

  return (
    <div ref={swatchesRef}>
      <button
        data-previous
        className="product-card__swatches-navigation product-card__swatches-navigation--previous"
        slot="container-start"
        ref={prevEl}
        data-hidden={!showArrows}
      >
        {caretPrevious}
      </button>
      <swiper-container
        ref={swiperRef}
        slides-per-view="auto"
        loop="true"
        center-insufficient-slides="true"
        events-prefix="product-card-swiper-"
        init="false"
      >
        {productGroup.map((product, i) => (
          <swiper-slide className="product-card__swatch-slide">
            <ProductCardSwatch
              product={product}
              active={product.id === activeProductId}
              setActiveProductId={setActiveProductId}
            />
          </swiper-slide>
        ))}
      </swiper-container>
      <button
        data-next
        className="product-card__swatches-navigation product-card__swatches-navigation--next"
        slot="container-end"
        ref={nextEl}
        data-hidden={!showArrows}
      >
        {caretNext}
      </button>
    </div>
  );
}
